import { Button } from "@/components/ui/Button";
import { Link } from "react-router-dom";
import { ArrowRight, RotateCw } from "lucide-react";
import { useEffect, useState } from "react";
import { capture } from "@/lib/analytics/posthog";

const Hero = () => {
    // tiny entrance animation toggle so the hero feels alive on first paint
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        const id = requestAnimationFrame(() => setMounted(true));
        return () => cancelAnimationFrame(id);
    }, []);

    return (
        <section className="relative w-full overflow-hidden isolate">
            {/* Layered gradient background — premium, modern, no library needed */}
            <div
                aria-hidden
                className="absolute inset-0 -z-10"
                style={{
                    background:
                        "radial-gradient(1200px 600px at 10% -10%, hsl(var(--secondary) / 0.35), transparent 60%), radial-gradient(900px 600px at 110% 10%, hsl(var(--gold) / 0.25), transparent 60%), linear-gradient(180deg, #F4F8FC 0%, #FFFFFF 100%)",
                }}
            />
            {/* Soft animated blobs */}
            <div
                aria-hidden
                className="hero-blob absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full opacity-40 blur-3xl"
                style={{ background: "hsl(var(--primary))", animation: "blobFloat1 18s ease-in-out infinite" }}
            />
            <div
                aria-hidden
                className="hero-blob absolute -bottom-32 right-[-80px] w-[480px] h-[480px] rounded-full opacity-30 blur-3xl"
                style={{ background: "hsl(var(--gold))", animation: "blobFloat3 22s ease-in-out infinite" }}
            />

            <div className="max-w-6xl mx-auto px-6 pt-[130px] md:pt-[160px] lg:pt-[180px] pb-20 md:pb-24 lg:pb-28">
                <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
                    {/* Left side - Copy + CTAs */}
                    <div
                        className={`flex-1 flex flex-col items-center lg:items-start text-center lg:text-left gap-6 transition-all duration-700 ${
                            mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                        }`}
                    >
                        {/* Eyebrow / urgency micro-pill */}
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur border border-primary/10 shadow-sm">
                            <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                            </span>
                            <span className="text-xs md:text-sm font-medium text-primary">
                                Always free for students · Secure payments
                            </span>
                        </div>

                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-balance">
                            <span className="text-primary">Tuition shouldn't decide</span>
                            <br />
                            <span className="text-primary">your future.</span>
                            <br />
                            <span
                                className="bg-clip-text text-transparent"
                                style={{
                                    backgroundImage:
                                        "linear-gradient(90deg, hsl(var(--gold)), hsl(var(--primary)))",
                                }}
                            >
                                Let your people invest in it.
                            </span>
                        </h1>

                        <p className="text-lg md:text-xl max-w-xl leading-relaxed text-slate-600">
                            Set up a free Fund Page in 60 seconds. Share your journey. Receive one-off or
                            monthly support — straight to your bank, no awkward asks.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-3 pt-1 w-full sm:w-auto">
                            <Link
                                to="/signup-creator"
                                className="w-full sm:w-auto"
                                onClick={() => capture("cta_clicked", { cta: "start_fund_page", location: "hero" })}
                            >
                                <Button
                                    size="lg"
                                    className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white text-base md:text-lg px-7 md:px-9 py-6 rounded-full font-semibold shadow-lg shadow-primary/20 group"
                                >
                                    Start your Fund Page
                                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </Button>
                            </Link>
                            <Link
                                to="/fund/demo"
                                className="w-full sm:w-auto"
                                onClick={() => capture("cta_clicked", { cta: "back_a_student", location: "hero" })}
                            >
                                <Button
                                    size="lg"
                                    className="w-full sm:w-auto text-base md:text-lg px-7 md:px-9 py-6 rounded-full font-semibold border-primary/30 text-primary hover:bg-primary/5 border bg-white/70 backdrop-blur"
                                >
                                    Back a student
                                </Button>
                            </Link>
                        </div>

                        <div className="flex items-center gap-4 text-sm text-slate-500 pt-1">
                            <div className="flex -space-x-2">
                                {/* TODO(content): swap for real supporter avatars */}
                                {["#FCD34D", "#60A5FA", "#34D399", "#F472B6"].map((c, i) => (
                                    <span
                                        key={i}
                                        className="inline-block w-7 h-7 rounded-full border-2 border-white"
                                        style={{ background: c }}
                                    />
                                ))}
                            </div>
                            <span>
                                Join a community of students backed by <span className="font-semibold text-primary">their people</span>.
                            </span>
                        </div>

                        <p className="text-xs md:text-sm text-slate-500">
                            Free for students. 5-7% platform fee for supporters. Payments via Stripe.
                        </p>
                    </div>

                    {/* Right side - Visual */}
                    <div
                        className={`flex-1 max-w-lg lg:max-w-none w-full transition-all duration-700 delay-100 ${
                            mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                        }`}
                    >
                        <div className="relative">
                            <div className="rounded-3xl overflow-hidden shadow-2xl shadow-primary/10 ring-1 ring-black/5">
                                <img
                                    src="/hero-students.jpg"
                                    alt="Students studying together"
                                    className="w-full h-auto object-cover aspect-[4/3]"
                                />
                            </div>

                            {/* Floating supporter card */}
                            <div className="absolute -bottom-5 -left-4 md:-left-8 bg-white rounded-2xl shadow-xl ring-1 ring-black/5 px-4 py-3 flex items-center gap-3 max-w-[260px]">
                                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center text-white text-xs font-bold">
                                    GL
                                </div>
                                <div className="text-left">
                                    {/* TODO(content): swap for live Firestore activity */}
                                    <p className="text-sm font-semibold text-slate-900 leading-tight">
                                        Grandma Lupe
                                    </p>
                                    <p className="text-xs text-slate-500 leading-tight">
                                        gave <span className="font-semibold text-emerald-600">$50/mo</span> to Maria
                                    </p>
                                </div>
                            </div>

                            {/* Floating monthly-recurring supporter card */}
                            <div className="absolute -top-5 -right-3 md:-right-6 bg-white rounded-2xl shadow-xl ring-1 ring-indigo-100 px-4 py-3 w-[230px]">
                                <div className="flex items-center gap-1.5 mb-2">
                                    <RotateCw className="w-3 h-3 text-indigo-500" />
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">
                                        Monthly support
                                    </span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                        UM
                                    </div>
                                    <div className="text-left flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-slate-900 leading-tight">
                                            Uncle Marcus
                                        </p>
                                        <p className="text-xs text-slate-500 leading-tight">
                                            <span className="font-semibold text-emerald-600">$70/mo</span> to Maria
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
